import {
  Alert,
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Grid, GridItem, Space} from 'platform/foundation';
import {z} from 'zod';

import {MoveAllMatricesFromCarisApiArg} from '@dms/api/core';
import {useMoveAllMatricesFromCarisMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

export function ConvertFromCebiaForm(props: RequiredTestIdProps) {
  const [moveFromCaris, {isLoading}] = useMoveAllMatricesFromCarisMutation();

  const handleSubmit: FormSubmitHandler<MoveAllMatricesFromCarisApiArg> = async (data) =>
    await moveFromCaris(data).unwrap().then(closeCurrentDialog).catch(handleApiError);

  return (
    <Form<MoveAllMatricesFromCarisApiArg> onSubmit={handleSubmit} experimentalZodSchema={schema}>
      {(control) => (
        <>
          <Grid columns={2}>
            <GridItem span={2}>
              <Alert
                message={i18n.t('page.accountingSettings.labels.moveFromCarisAlertMessage')}
                variant="warning"
                data-testid={suffixTestId('moveFromCarisAlertMessage', props)}
              />
            </GridItem>

            <FormField
              type="text"
              control={control}
              name="googleDocId"
              isRequired
              label={i18n.t('page.accountingSettings.labels.googleDocId')}
              data-testid={suffixTestId('googleDocId', props)}
            />

            <FormField
              type="text"
              control={control}
              name="sheetName"
              isRequired
              label={i18n.t('page.accountingSettings.labels.sheetName')}
              data-testid={suffixTestId('sheetName', props)}
            />

            <FormField
              type="apiDate"
              control={control}
              name="validFrom"
              isRequired
              label={i18n.t('page.accountingSettings.labels.validFrom')}
              data-testid={suffixTestId('validFrom', props)}
            />

            <FormField
              type="apiDate"
              control={control}
              name="validTo"
              isRequired
              label={i18n.t('page.accountingSettings.labels.validTo')}
              data-testid={suffixTestId('validTo', props)}
            />
          </Grid>

          <Space vertical={4} />
          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.discard')}
              onClick={closeCurrentDialog}
              variant="secondary"
              data-testid={suffixTestId('discard', props)}
            />
            <FormButton
              title={i18n.t('general.actions.convert')}
              control={control}
              type="submit"
              isLoading={isLoading}
              data-testid={suffixTestId('convert', props)}
            />
          </ButtonGroup>
        </>
      )}
    </Form>
  );
}

const schema = z.object({
  googleDocId: z.string(),
  sheetName: z.string(),
  validTo: z.string(),
  validFrom: z.string(),
});
